const AUTH_TOKEN_KEY = "authToken";
const AUTH_USER_KEY = "user";
const SESSION_EXPIRED_EVENT = "auth:session-expired";
let authFetchInterceptorInstalled = false;

type JwtPayload = {
  exp?: number;
};

const parseJwtPayload = (token: string): JwtPayload | null => {
  try {
    const [, payload] = token.split(".");

    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      "=",
    );

    return JSON.parse(window.atob(paddedPayload)) as JwtPayload;
  } catch {
    return null;
  }
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const clearAuthStorage = (): void => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
};

export const isTokenExpired = (token: string): boolean => {
  const payload = parseJwtPayload(token);

  if (!payload?.exp) {
    return false;
  }

  return payload.exp * 1000 <= Date.now();
};

export const handleSessionExpired = (
  message = "Your session has expired. Please sign in again.",
): void => {
  clearAuthStorage();

  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, {
      detail: { message },
    }),
  );
};

export const authEvents = {
  sessionExpired: SESSION_EXPIRED_EVENT,
};

export const installAuthFetchInterceptor = (apiBaseUrl: string): void => {
  if (authFetchInterceptorInstalled) {
    return;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const token = getAuthToken();

    if (token && requestUrl.startsWith(apiBaseUrl) && isTokenExpired(token)) {
      handleSessionExpired();
      throw new Error("Session expired. Please login again.");
    }

    const response = await originalFetch(input, init);

    if (requestUrl.startsWith(apiBaseUrl) && response.status === 401) {
      let message = "Session expired. Please login again.";

      try {
        const clonedResponse = response.clone();
        const contentType = clonedResponse.headers.get("content-type");

        if (contentType && contentType.includes("application/json")) {
          const data = await clonedResponse.json();
          message = data?.message || message;
        }
      } catch {
        // Ignore response parsing failures while handling session expiry.
      }

      handleSessionExpired(message);
    }

    return response;
  };

  authFetchInterceptorInstalled = true;
};
